import { Calendar, Envelope, TelegramLogo } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import Link from "next/link"
import { twMerge } from "tailwind-merge"
import Container from "../common/Container"
import Layout from "../common/Layout"
import MarketingHeading from "../common/MarketingHeading"
import CustomerGrid from "../home/CustomerGrid"
import { ProductsList } from "../home/SolutionsForEveryScale"
import PricingCards from "./PricingCards"
import PricingTable from "./PricingTable"

// export const headingCss = style([
//   atoms({
//     fontWeight: "500",
//     textAlign: "center",
//   }),
//   {
//     fontSize: "54px",
//     letterSpacing: "-2px",
//     lineHeight: ".9em",
//     "@media": {
//       [`(max-width: ${breakpoints.tablet}px)`]: {
//         fontSize: "36px",
//       },
//     },
//   },
// ])

const faqData = {
  faq1: {
    question: "How do I get an API key?",
    answer: (
      <span className="text-base">
        <Link style={{ fontSize: "inherit" }} href="/explorer/account/billing">
          Sign up
        </Link>{" "}
        for our API plan in your {`organization's`} billing page. After you subscribe to the Token
        Terminal API, you will find your API key on both the{" "}
        <Link
          style={{ fontSize: "inherit" }}
          href="https://tokenterminal.com/docs/api-reference/introduction"
          className="default-text-size"
        >
          API reference
        </Link>{" "}
        and on your personal{" "}
        <Link
          style={{ fontSize: "inherit" }}
          href="/explorer/account/api"
          className="default-text-size"
        >
          account page
        </Link>
        .
      </span>
    ),
  },
  faq2: {
    question: "Can I talk to someone from the team?",
    answer: (
      <span className="text-base">
        Yes, send us an email at{" "}
        <Link
          style={{ fontSize: "inherit" }}
          href="mailto:people@tokenterminal.xyz"
          className="default-text-size"
        >
          people@tokenterminal.xyz
        </Link>
        .
      </span>
    ),
  },
  faq3: {
    question: "How do I get an invoice for my subscription?",
    answer: (
      <span className="text-base">
        Reach out to{" "}
        <Link
          style={{ fontSize: "inherit" }}
          href="mailto:people@tokenterminal.xyz"
          className="default-text-size"
        >
          finance@tokenterminal.xyz
        </Link>{" "}
        and we’ll email you the invoice.
      </span>
    ),
  },
  faq4: {
    question: "Where can I find your terms of service?",
    answer: (
      <span className="text-base">
        We make use of a standard set of general terms that apply to all of our users. You can find
        them here:{" "}
        <Link
          style={{ fontSize: "inherit" }}
          href="https://tokenterminal.com/docs/terms"
          className="default-text-size"
        >
          tokenterminal.com/docs/terms
        </Link>
        .
      </span>
    ),
  },
  faq5: {
    question: "Can I get access for my team?",
    answer: (
      <span className="text-base">
        Yes, we offer team-wide subscriptions. Invite team members from your{" "}
        <Link
          style={{ fontSize: "inherit" }}
          href="/explorer/account/"
          className="default-text-size"
        >{`organization's`}</Link>{" "}
        settings page. You will be charged monthly for each user you add to your team.
      </span>
    ),
  },
  faq6: {
    question: "Can I cancel my subscription anytime?",
    answer: (
      <span className="text-base">
        Yes, you can cancel your subscription at any time, and it will remain active until the end
        of the current billing cycle, which is either the end of month or year, depending on the
        subscription. The subscription will not automatically renew. You will not incur any
        additional charges, unless you decide to purchase a new subscription.
      </span>
    ),
  },
}

const PricingPage = () => {
  return (
    <Layout>
      <div className="flex flex-col items-start w-full relative">
        <Container
          className={twMerge(
            "flex flex-col gap-12 items-center",
            "pt-[100px] text-center relative",
          )}
        >
          <div className="flex flex-col gap-2 px-4 md:px-0">
            <MarketingHeading level={1} className="text-[36px] md:text-[54px] tracking-tighter">
              Choose your plan
            </MarketingHeading>
            <span className="text-base text-fg-secondary max-w-[620px] w-full">
              We provide free access to all of our core data. Upgrade for data customization, data
              downloads, and integrations into your workflows via Sheets, API, MCP, and BigQuery.
              All paid plans come with dedicated data support.
            </span>
          </div>
        </Container>
      </div>
      <Container
        className="flex flex-col items-center justify-center"
        style={{ paddingTop: "100px", paddingBottom: "24px" }}
      >
        <PricingCards />
      </Container>
      <Container>
        <Card
          className="p-7"
          style={{
            borderRadius: "16px",
          }}
        >
          <div className="flex flex-row w-full flex-wrap gap-4 justify-between items-center">
            <div className="flex flex-col gap-1">
              <span className="font-medium text-base text-fg-default">
                Need a custom plan or not sure which one is right for you?
              </span>
              <span className="text-sm text-fg-secondary">
                We&apos;re happy to help you find the right fit for you or your team.
              </span>
            </div>
            <LinkButton href="/contact">Contact sales</LinkButton>
          </div>
        </Card>
      </Container>
      <Container style={{ paddingTop: "100px", paddingBottom: "50px" }}>
        <PricingTable />
      </Container>
      <Container className="py-[50px] md:py-[100px]">
        <Card className="p-[50px] md:p-[150px] bg-bg-surface w-full rounded-2xl px-12 py-16">
          <div className="flex flex-col gap-8 items-center justify-center">
            <MarketingHeading
              level={2}
              className="text-[36px] md:text-[46px] font-medium text-center leading-[0.9em]"
            >
              Talk to our team
            </MarketingHeading>
            <div
              className="flex flex-col gap-4 items-center justify-center"
              style={{ width: "300px", maxWidth: "100%" }}
            >
              <LinkButton
                size="large"
                className="w-full"
                target="_blank"
                href="https://t.me/TokenTerminalHQ"
              >
                <Icon as={TelegramLogo} />
                Chat on Telegram
              </LinkButton>
              <LinkButton
                size="large"
                className="w-full"
                target="_blank"
                href="https://calendly.com/token-terminal/intro-call"
              >
                <Icon as={Calendar} />
                Book a demo
              </LinkButton>
              <LinkButton
                size="large"
                className="w-full"
                target="_blank"
                href="mailto:people@tokenterminal.xyz?subject=Contact Token Terminal"
              >
                <Icon as={Envelope} />
                Email us
              </LinkButton>
            </div>
          </div>
        </Card>
      </Container>
      <Container className="py-[50px] md:pt-[100px] md:pb-[100px]">
        <div className="flex flex-row justify-center mb-10">
          <MarketingHeading level={2}>Learn more about our products</MarketingHeading>
        </div>
        <ProductsList />
      </Container>
      <Container className="py-10">
        <CustomerGrid />
      </Container>
      <Container style={{ padding: "100px 0" }}>
        <div className="flex flex-col items-center">
          <MarketingHeading level={2} className="mb-12 text-center">
            The most common questions
          </MarketingHeading>
          <div className="flex flex-col gap-4 w-full">
            {Object.values(faqData).map((faq, index) => (
              <Card
                key={index}
                className="p-6"
                style={{
                  borderRadius: "16px",
                  position: "relative",
                }}
              >
                <div className="flex flex-col gap-1">
                  <span className="text-base font-medium text-fg-default">{faq.question}</span>
                  <div className="text-fg-secondary">{faq.answer}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </Container>
    </Layout>
  )
}

export default PricingPage
